/* eslint-disable @typescript-eslint/no-explicit-any */
// Must be first. Every `Reflect.getMetadata` / `Reflect.defineMetadata` /
// `Reflect.hasOwnMetadata` call below depends on this polyfill, and the decorators in
// this file run at module-evaluation time. Importing it here (rather than relying on
// main.ts alone) keeps the polyfill ordered ahead of this module in the production
// build, where code splitting can otherwise evaluate this chunk first.
import 'reflect-metadata';

import {
  ContainerModule,
  decorate, injectable, interfaces, METADATA_KEY as INVERSIFY_METADATA_KEY,
} from 'inversify';

export enum METADATA_KEY {
  FACTORY = 'inversify:@zoll/factory',
  VUE_PLUGIN = 'inversify:@zoll/vuePlugin',
}

export type InferMetadataTargetInstance<
  T extends interfaces.Newable<any>,
> = T extends interfaces.Newable<infer TInstance> ? TInstance : never;

export type MetadataTargetConstructor<T = any> = interfaces.Newable<T>;

export type MetadataBindConstraint<
  TTarget extends interfaces.Newable<any> = interfaces.Newable<any>,
  TReturn = any,
> =
  (
    bind: (serviceIdentifier: interfaces.ServiceIdentifier<TReturn>) => interfaces.BindingToSyntax<TReturn>,
    target: InferMetadataTargetInstance<TTarget>
  ) => interfaces.BindingWhenOnSyntax<TReturn>;

export interface MetadataSyntax<
  TTarget extends interfaces.Newable<any> = interfaces.Newable<any>,
  TReturn = any,
> {
  constraint: MetadataBindConstraint<TTarget, TReturn>;
  implementationType: TTarget;
}

export const isAlreadyDecorated = (target: any) => Reflect
  .hasOwnMetadata(INVERSIFY_METADATA_KEY.PARAM_TYPES, target as object);

export const buildMetadataModule = (metadataKey: METADATA_KEY) => new ContainerModule((bind) => {
  const metadata = <MetadataSyntax[]> Reflect.getMetadata(metadataKey, Reflect) || [];
  metadata.map((m) => m.constraint(bind, m.implementationType));
});

export const validateDecorator = (target: any, metadataKey: METADATA_KEY, force = false) => {
  const redecorateWithInject = force === true;

  if (redecorateWithInject === true && isAlreadyDecorated(target) === false) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    decorate(injectable(), target);
  }
  else if (redecorateWithInject === true && isAlreadyDecorated(target) === true) {
    // Do nothing
  }
  else {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      decorate(injectable(), target);
    }
    catch (e) {
      throw new Error(
        `Cannot apply @${metadataKey} decorator multiple times but is has been used ` +
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        `multiple times in ${target.name} ` +
        `Please use @${metadataKey}(ID, true) if you are trying to declare multiple bindings!`,
      );
    }
  }
};

export const metadataDecorator = <
  TReturn = any,
  TTarget extends interfaces.Newable<any> = interfaces.Newable<any>,
  ServiceIdentifier = interfaces.ServiceIdentifier<TReturn>,
> (
    metadataKey: METADATA_KEY,
    serviceIdentifier: ServiceIdentifier,
    target: TTarget,
    force = false,
  ): MetadataSyntax<TTarget, TReturn> => {
  validateDecorator(target, metadataKey, force);

  const currentMetadata = <MetadataSyntax<TTarget, TReturn>> {
    constraint() {
      throw new Error('You must override the metadataDecorator constraint');
    },
    implementationType: target,
  };

  const previousMetadata = <MetadataSyntax<TTarget, TReturn>[]> Reflect.getMetadata(
    metadataKey,
    Reflect,
  ) || [];

  Reflect.defineMetadata(metadataKey, [currentMetadata, ...previousMetadata], Reflect);

  return currentMetadata;
};
